/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import type { AmforiSyncReportEntry } from '@/api/integrations/amfori/types';
import { ReportCounters } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportCounters';
import { ReportEntryCard } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportEntryCard';
import { ReportEntryTable } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportEntryTable';
import { ReportFailureCallout } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportFailureCallout';
import { ReportFilters } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportFilters';
import { ReportStatusHeader } from '@/app/(client)/network/-components/companyProfileTabs/integrations/report/ReportStatusHeader';
import { useAmforiSyncReportPage } from '@/app/(client)/network/-hooks/useAmforiSyncReportPage';
import {
  countEntries,
  filterEntries,
  needsAttention,
} from '@/app/(client)/network/-utils/reportView';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslator } from '@/hooks/useTranslator';
import { useSessionStore } from '@/store/sessionStore';

export const Route = createFileRoute(
  '/(client)/network/(pages)/(detail)/$companyId/integrations/history/$syncHistoryId/',
)({
  component: AmforiSyncReportPage,
});

function AmforiSyncReportPage() {
  const { syncHistoryId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'integrations' });
  const user = useSessionStore(s => s.user);
  const ownCompanyId = user?.companyId;
  const supportEmail =
    user?.company?.companyType === 'CLIENT'
      ? 'department-customer-success@retraced.com'
      : 'team-customer-service-team@retraced.com';

  const {
    report,
    filter,
    setFilter,
    view,
    setView,
    isDownloading,
    isRerunning,
    handleDownload,
    handleRerun,
  } = useAmforiSyncReportPage(syncHistoryId);

  if (report.isLoading) {
    return (
      <div className='flex flex-1 items-center justify-center p-16'>
        <Loader2 className='size-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (report.isError || !report.data) {
    return (
      <div className='p-8'>
        <EmptyState
          title={t('report.loadError')}
          {...(report.error instanceof Error
            ? { description: report.error.message }
            : {})}
        />
      </div>
    );
  }

  const { header, counters, entries } = report.data;
  const counts = countEntries(entries);
  const filtered = filterEntries(entries, filter);

  const renderCard = (entry: AmforiSyncReportEntry) => (
    <ReportEntryCard
      key={entry.id}
      entry={entry}
      ownCompanyId={ownCompanyId}
      onRerun={handleRerun}
      isRerunning={isRerunning}
    />
  );

  const renderQueueSection = (
    titleKey: string,
    sectionEntries: AmforiSyncReportEntry[],
  ) =>
    sectionEntries.length > 0 ? (
      <div className='flex flex-col gap-4'>
        <h4 className='text-sm font-semibold'>{t(titleKey)}</h4>
        {sectionEntries.map(renderCard)}
      </div>
    ) : null;

  const renderQueue = () => {
    if (filtered.length === 0) {
      return <EmptyState title={t('report.empty.title')} />;
    }
    if (filter === 'all') {
      return (
        <div className='flex flex-col gap-6'>
          {renderQueueSection(
            'report.section.attention',
            entries.filter(needsAttention),
          )}
          {renderQueueSection(
            'report.section.success',
            entries.filter(entry => !needsAttention(entry)),
          )}
        </div>
      );
    }
    const titleKey =
      filter === 'ok' ? 'report.section.success' : 'report.section.attention';
    return renderQueueSection(titleKey, filtered);
  };

  return (
    <div className='flex flex-col gap-6 p-6'>
      <ReportStatusHeader
        header={header}
        onDownload={handleDownload}
        isDownloading={isDownloading}
      />
      {header.lastError ? (
        <ReportFailureCallout
          lastError={header.lastError}
          reportId={header.syncHistoryId}
          ownCompanyId={ownCompanyId}
          supportEmail={supportEmail}
        />
      ) : null}
      <ReportCounters counters={counters} />
      <ReportFilters
        counts={counts}
        filter={filter}
        onFilterChange={setFilter}
        view={view}
        onViewChange={setView}
      />
      {view === 'table' ? (
        <ReportEntryTable
          entries={filtered}
          ownCompanyId={ownCompanyId}
          onRerun={handleRerun}
          isRerunning={isRerunning}
        />
      ) : (
        renderQueue()
      )}
    </div>
  );
}
